/* eslint-disable id-match */
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { object, string, number as Yup_number } from 'yup';
import { PASSENGER } from '../../../brands/default/content/components/checkout';
import { addPassenger, isTravelValid } from '../../../helpers/basketTravel';
import { validateName } from '../../../helpers/validations';
import ThemeContext from '../../../providers/ThemeProvider';
import { setIsTravelValid } from '../../../redux/actions/checkout';
import BareSelect from '../../_generic/form/BareSelect';
import InputForm from '../../_generic/input/Input';

// create days
const days = [];
days.push({ name: PASSENGER.selectDays, value: '' });
for (let index = 1; index < 32; index++) {
  days.push({ name: index, value: index });
}

// create short months
const shortMonth = [];
shortMonth.push({ name: PASSENGER.selectMonths, value: '' });
for (let index = 0; index < 12; index++) {
  const month = dayjs().set('month', index).format('MMM');
  shortMonth.push({ name: month, value: index });
}

// current year
const currentYear = dayjs().format('YYYY');

// eslint-disable-next-line sonarjs/cognitive-complexity
const Passenger = ({ number, dealId, product }) => {
  const theme = useContext(ThemeContext);
  const dispatch = useDispatch();
  const storageKey = `passenger-${dealId}-${number}`;
  const hasPassenger = JSON.parse(localStorage.getItem(storageKey)) || {};
  const formik = useFormik({
    initialValues: {
      days: hasPassenger.days || '',
      firstName: hasPassenger.firstName || '',
      lastName: hasPassenger.lastName || '',
      months: hasPassenger.months || '',
      title: hasPassenger.title || '',
      years: hasPassenger.years || '',
    },
    validationSchema: object().shape({
      days: Yup_number().required('Required'),
      firstName: string()
        .required('Required')
        .test((value) => {
          return validateName(value);
        }),
      lastName: string()
        .required('Required')
        .test((value) => {
          return validateName(value);
        }),
      months: string().required('Required'),
      title: string().required('Required'),
      years: Yup_number().required('Required'),
    }),
  });
  const {
    handleChange,
    handleBlur,
    touched,
    values,
    errors,
    isValid,
    validateForm,
  } = formik;

  useEffect(() => {
    if (product && number && values && values.lastName) {
      addPassenger(product, number, isValid, values);
      dispatch(setIsTravelValid(isTravelValid(product)));
    }
  }, [product, number, values]);

  useEffect(() => {
    // formik validation
    validateForm();
  }, []);

  // update storage
  const updateStorage = (event, fieldName) => {
    handleBlur(event);
    hasPassenger[fieldName] = values[fieldName];
    localStorage.setItem(storageKey, JSON.stringify(hasPassenger));
    addPassenger(product, number, isValid, values);
    dispatch(setIsTravelValid(isTravelValid(product)));
  };

  return (
    <div className="passenger">
      <h3 className="passenger__heading">{PASSENGER.heading(number)}</h3>
      <div className="passenger__row">
        <div className="passenger__title-col">
          <BareSelect
            ariaLabel="title"
            dataqa="title"
            error={errors.title && touched.title}
            extraClasses="passenger__select"
            name="title"
            onBlur={(event) => {
              updateStorage(event, 'title');
            }}
            onChange={handleChange}
            options={PASSENGER.selectTitles}
            value={values.title}
          />
        </div>
        <div className="passenger__fname-col">
          <InputForm
            error={errors.firstName && touched.firstName}
            inputValue={values.firstName}
            name="firstName"
            onBlurFunction={(event) => {
              updateStorage(event, 'firstName');
            }}
            onChangeFunction={handleChange}
            placeholder={PASSENGER.placeholderFirstName}
            type="text"
          />
        </div>
      </div>
      <div className="passenger__row">
        <div className="passenger__lname-col">
          <InputForm
            error={errors.lastName && touched.lastName}
            inputValue={values.lastName}
            name="lastName"
            onBlurFunction={(event) => {
              updateStorage(event, 'lastName');
            }}
            onChangeFunction={handleChange}
            placeholder={PASSENGER.placeholderLastName}
            type="text"
          />
        </div>
      </div>
      <div className="passenger__row">
        <label className="passenger__label">{PASSENGER.labelDob}</label>
      </div>
      <div className="passenger__row">
        <div className="passenger__day-col">
          <BareSelect
            ariaLabel="days"
            dataqa="days"
            error={errors.days && touched.days}
            extraClasses="passenger__select"
            name="days"
            onBlur={(event) => {
              updateStorage(event, 'days');
            }}
            onChange={handleChange}
            options={days}
            value={values.days}
          />
        </div>
        <div className="passenger__month-col">
          <BareSelect
            ariaLabel="months"
            dataqa="months"
            error={errors.months && touched.months}
            extraClasses="passenger__select"
            name="months"
            onBlur={(event) => {
              updateStorage(event, 'months');
            }}
            onChange={handleChange}
            options={shortMonth}
            value={values.months}
          />
        </div>
        <div className="passenger__year-col">
          <InputForm
            error={errors.years && touched.years}
            inputValue={values.years}
            max={currentYear}
            min="1900"
            name="years"
            onBlurFunction={(event) => {
              updateStorage(event, 'years');
            }}
            onChangeFunction={handleChange}
            placeholder={PASSENGER.placeholderYears}
            type="number"
          />
        </div>
      </div>
      <style jsx>{`
        .passenger {
          width: 50%;
          padding: 0 15px 15px 15px;
        }
        .passenger__heading {
          font-size: 18px;
          margin-bottom: 15px;
          color: #333;
          font-weight: 500;
        }
        .passenger__row {
          display: flex;
        }
        .passenger__title-col,
        .passenger__day-col {
          width: 28%;
        }
        .passenger__fname-col {
          width: 72%;
        }
        .passenger__lname-col {
          width: 100%;
        }
        .passenger__month-col {
          width: 30%;
        }
        .passenger__year-col {
          width: 42%;
        }
        .passenger__fname-col,
        .passenger__month-col,
        .passenger__year-col {
          padding-left: 8px;
        }
        .passenger :global(.passenger__select) {
          min-width: 56px;
          min-height: 42px;
          background-color: transparent;
          padding: 10px 12px;
          font-size: 14px;
          color: #333;
        }
        .passenger__label {
          display: inline-block;
          max-width: 100%;
          margin-bottom: 5px;
          font-weight: 700;
        }
        @media (max-width: ${theme.breakpoints.mdDown}) {
          .passenger {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

Passenger.propTypes = {
  dealId: PropTypes.number.isRequired,
  number: PropTypes.number.isRequired,
  product: PropTypes.object,
};

export default Passenger;
